import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Check, ArrowRight, Download, Settings } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import Button from '../components/common/Button'

function PayPalSuccess() {
  const [searchParams] = useSearchParams()
  const subscriptionId = searchParams.get('subscription_id') || searchParams.get('subscriptionID')
  const orderId = searchParams.get('order_id') || searchParams.get('token')
  const tierId = searchParams.get('tier_id') || 'Professional'
  const billing = searchParams.get('billing') || 'monthly'

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  return (
    <>
      <Helmet>
        <title>Payment Successful - HireFlow</title>
        <meta name="description" content="Your payment was successful. Welcome to HireFlow!" />
      </Helmet>

      <section className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-success-50 py-20">
        <div className="container-custom max-w-3xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="card p-12 text-center"
          >
            {/* Success Icon */}
            <div className="w-20 h-20 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-success-600" />
            </div>

            {/* Main Heading */}
            <h1 className="heading-lg mb-4">Payment Successful!</h1>
            <p className="text-xl text-neutral-600 mb-8">
              Thank you for {tierId === 'lifetime' ? 'purchasing' : 'subscribing to'} HireFlow {tierId.charAt(0).toUpperCase() + tierId.slice(1)}. Your account has been upgraded.
            </p>

            {/* Payment Details */}
            <div className="bg-neutral-50 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-neutral-900 mb-4">Payment Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-neutral-600">Payment Method</span>
                  <span className="font-medium text-neutral-900">PayPal</span>
                </div>
                {subscriptionId && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Subscription ID</span>
                    <span className="font-mono text-sm text-neutral-900">{subscriptionId}</span>
                  </div>
                )}
                {orderId && (
                  <div className="flex justify-between">
                    <span className="text-neutral-600">Order ID</span>
                    <span className="font-mono text-sm text-neutral-900">{orderId}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-neutral-600">Status</span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-success-100 text-success-700">
                    Active
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600">Plan</span>
                  <span className="font-medium text-neutral-900">
                    {tierId.charAt(0).toUpperCase() + tierId.slice(1)} {tierId !== 'lifetime' && `(${billing})`}
                  </span>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-primary-50 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-neutral-900 mb-4">Next Steps</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <Download className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-700">
                    Install the HireFlow Chrome Extension if you haven't already
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <Settings className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-700">
                    Log in to the extension to sync your subscription
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <ArrowRight className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-700">
                    Start creating unlimited professional resumes!
                  </span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="primary"
                size="lg"
                href="https://chromewebstore.google.com/detail/hireflow-linkedin-to-resu/mlpiocpnoigheicpmdolcfehlmpnhcob"
                external
              >
                Install Extension
                <Download className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="secondary"
                size="lg"
                href="/"
              >
                Return to Home
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Receipt Info */}
            <p className="text-sm text-neutral-500 mt-8">
              A receipt has been sent to your email. You can manage your subscription anytime through the extension.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default PayPalSuccess
